import { showDialogInfo } from "@/hooks/use-dialog-info"
import { DayObject } from "@/types/date"
import {
  ConfirmOption,
  ItineraryEvent,
  ItineraryEventCreation,
  ItineraryEventUpdates,
} from "@/types/itinerary"
import { TripRef } from "@/types/trip"
import { createAsyncThunk } from "@reduxjs/toolkit"

import { toastManager } from "@/lib/toast"
import { showGenericErrorToast } from "@/lib/toast-utils"
import { assertPresent } from "@/lib/utils/assertions"
import { updateChatOnCreateOrUpdateItineraryEvent } from "../chat"
import { dayAfter, dayBefore, sameDay } from "../date-utils"
import { handleTripUpdateError } from "../itinerary-error-handers"
import { createItineraryActions } from "../itinerary-ui-actions"
import { startDay } from "../itinerary-utils"
import { isSerializedClientError } from "./api/base-query"
import { itineraryApi } from "./api/itinerary-api"
import { selectTripInSheetStack } from "./detail-sheet-slice"
import { removeEvents } from "./itinerary-remove-events-thunks"
import itinerarySlice, {
  selectAllPersistentEvents,
  selectDayOperationPending,
  selectEventsStartingOnDayNumber,
  selectNormalItineraryDays,
} from "./itinerary-slice"
import { AppDispatch, RootState } from "./redux-store"
import { selectTripRef } from "./trip-slice"

export const createEvent = createAsyncThunk<
  { event: ItineraryEvent | undefined; tripRef: TripRef },
  {
    params: ItineraryEventCreation
    tripRef: TripRef
  },
  { dispatch: AppDispatch; state: RootState }
>(
  "itineraryThunks/createEvent",
  async ({ params, tripRef }, { dispatch, getState }) => {
    const currentTripRef = selectTripRef(getState())
    try {
      const response = await dispatch(
        itineraryApi.endpoints.createEvent.initiate({
          tripRef,
          params,
        })
      ).unwrap()
      if (currentTripRef === tripRef) {
        updateChatOnCreateOrUpdateItineraryEvent(dispatch, response)
      }
      return { event: response.event, tripRef }
    } catch (err) {
      if (currentTripRef === tripRef) {
        handleTripUpdateError(err, selectAllPersistentEvents(getState()))
      } else {
        toastManager.add({
          type: "error",
          description: "Sorry, there was a problem adding to your trip.",
        })
      }
      return { event: undefined, tripRef }
    }
  }
)

export const updateEvent = createAsyncThunk<
  void,
  {
    tripRef: TripRef
    eventId: number
    params: ItineraryEventUpdates
    confirmed?: ConfirmOption
  },
  { dispatch: AppDispatch; state: RootState }
>(
  "itineraryThunks/updateEvent",
  async ({ tripRef, eventId, params, confirmed }, { dispatch, getState }) => {
    try {
      const response = await dispatch(
        itineraryApi.endpoints.updateEvent.initiate({
          tripRef,
          eventId,
          updates: params,
          confirmed,
        })
      ).unwrap()
      updateChatOnCreateOrUpdateItineraryEvent(dispatch, response)
    } catch (err) {
      if (isSerializedClientError(err, "ConfirmationRequired") && !confirmed) {
        const errorData = err.data as {
          movable_event_ids: number[]
          extendable_event_ids: number[]
        }
        if (errorData.movable_event_ids.includes(eventId)) {
          showDialogInfo({
            title: "Move event?",
            message: "You are about to move a booked event.",
            cancelLabel: "No, keep it",
            okLabel: "Yes, move",
            okVariant: "primary",
            okAction: () => {
              // Retry with confirmed set to confirm_move
              void dispatch(
                updateEvent({
                  tripRef,
                  eventId,
                  params,
                  confirmed: "confirm_move",
                })
              )
            },
          })
          return
        } else if (errorData.extendable_event_ids.includes(eventId)) {
          showDialogInfo({
            title: "Extend trip?",
            message: "The trip needs to be extended to include the event.",
            cancelLabel: "No, keep the trip dates",
            okLabel: "Yes, extend",
            okVariant: "primary",
            okAction: () => {
              // Retry with confirmed set to confirm_extend
              void dispatch(
                updateEvent({
                  tripRef,
                  eventId,
                  params,
                  confirmed: "confirm_extend",
                })
              )
            },
          })
          return
        }
      }
      handleTripUpdateError(err, selectAllPersistentEvents(getState()))
    }
  }
)

export const insertDay = createAsyncThunk<
  void,
  { day: DayObject | number; count?: number },
  { dispatch: AppDispatch; state: RootState }
>(
  "itineraryThunks/insertDay",
  async ({ day, count }, { dispatch, getState }) => {
    const tripRef = selectTripRef(getState())
    if (!tripRef) return
    if (selectDayOperationPending(getState())) return

    let dayObject: DayObject
    if (typeof day === "number") {
      const days = selectNormalItineraryDays(getState())
      if (days.length === 0) {
        dayObject = dayBefore(days[0].day)
      } else {
        dayObject =
          days.find((d) => d.dayNumber === day)?.day ||
          dayAfter(days[days.length - 1].day)
      }
    } else {
      dayObject = day
    }
    dispatch(itinerarySlice.actions.setDayOperationPending(true))
    try {
      await dispatch(
        itineraryApi.endpoints.insertDay.initiate({
          tripRef,
          date: dayObject,
          count,
        })
      ).unwrap()
    } catch (err) {
      handleTripUpdateError(err, selectAllPersistentEvents(getState()))
    } finally {
      dispatch(itinerarySlice.actions.setDayOperationPending(false))
    }
  }
)

export const removeDay = createAsyncThunk<
  void,
  {
    day: DayObject | number
    count?: number
    confirm?: boolean
  },
  { dispatch: AppDispatch; state: RootState }
>(
  "itineraryThunks/removeDay",
  async ({ day, count, confirm }, { dispatch, getState }) => {
    confirm ??= true
    const dayObject =
      typeof day === "number"
        ? assertPresent(
            selectNormalItineraryDays(getState()).find(
              (d) => d.dayNumber === day
            )?.day
          )
        : day

    const tripRef = selectTripRef(getState())
    if (!tripRef) return
    const events = selectAllPersistentEvents(getState())

    const doRemove = async () => {
      if (selectDayOperationPending(getState())) return

      dispatch(itinerarySlice.actions.setDayOperationPending(true))
      try {
        await dispatch(
          itineraryApi.endpoints.removeDay.initiate({
            tripRef,
            date: dayObject,
            count,
          })
        ).unwrap()
      } catch (err) {
        handleTripUpdateError(err, events)
      } finally {
        dispatch(itinerarySlice.actions.setDayOperationPending(false))
      }
    }

    if (
      confirm &&
      events &&
      events.some((event) => sameDay(startDay(event), dayObject))
    ) {
      showDialogInfo({
        title: "Remove day",
        message:
          "Are you sure you want to remove this day and its events from your itinerary?",
        okLabel: "Delete",
        cancelLabel: "Cancel",
        okAction: () => void doRemove(),
      })
    } else {
      void doRemove()
    }
  }
)

export const unscheduleEvents = createAsyncThunk<
  void,
  TripRef,
  { dispatch: AppDispatch; state: RootState }
>("itineraryThunks/unscheduleEvents", async (tripRef, { dispatch }) => {
  try {
    dispatch(itinerarySlice.actions.setUnschedulingEvents(true))
    await dispatch(
      itineraryApi.endpoints.unscheduleEvents.initiate(tripRef)
    ).unwrap()
  } catch (error) {
    console.error("Error unscheduling events:", error)
    showGenericErrorToast()
  } finally {
    dispatch(itinerarySlice.actions.setUnschedulingEvents(false))
  }
})

export const confirmRemoveIdeas = createAsyncThunk<
  void,
  { tripRef: TripRef },
  { dispatch: AppDispatch; state: RootState }
>(
  "itineraryThunks/confirmRemoveIdeas",
  async ({ tripRef }, { dispatch, getState }) => {
    const tripSheetOpen = selectTripInSheetStack(getState())
    showDialogInfo({
      title: "Remove ideas?",
      message: "This will remove all your ideas from your trip.",
      okAction: () => {
        void (async () => {
          dispatch(itinerarySlice.actions.setRemovingIdeas(true))
          try {
            await createItineraryActions({
              dispatch,
              tripRef,
              tripSheetOpen,
            }).removeIdeas()
          } finally {
            dispatch(itinerarySlice.actions.setRemovingIdeas(false))
          }
        })()
      },
      cancelLabel: "Cancel",
      okLabel: "Remove",
    })
  }
)

export const clearDay = createAsyncThunk<
  void,
  { dayNumber: number; confirm?: boolean },
  { dispatch: AppDispatch; state: RootState }
>(
  "itineraryThunks/clearDay",
  async ({ dayNumber, confirm }, { dispatch, getState }) => {
    confirm ??= true
    const events = selectEventsStartingOnDayNumber(getState(), dayNumber)
    const doRemoveEvents = async () => {
      try {
        await dispatch(removeEvents(events.map((e) => e.id))).unwrap()
      } catch {
        // removeEvents presents the error before rejecting.
      }
    }

    if (events.length > 0 && confirm) {
      showDialogInfo({
        title: "Clear day",
        message:
          "Are you sure you want to remove all of the events from this day?",
        okLabel: "Clear",
        cancelLabel: "Cancel",
        okAction: () => void doRemoveEvents(),
      })
    } else {
      void doRemoveEvents()
    }
  }
)
